import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { ToastService } from '../../shared/services/toast-service';
import { ModalService } from '../../shared/components/modal/modal.service';
import { LanguageService } from '../../core/i18n/language.service';
import { LayoutService } from './services/layout.service';
import { Layout, LayoutGroup, LayoutGroupRequest, LayoutRequest } from './models/layout.model';
import { LayoutGroupDialogComponent, DialogMode } from './layout-group-dialog/layout-group-dialog.component';
import { LayoutDialogComponent, LayoutDialogMode } from './layout-dialog/layout-dialog.component';
import { AssignDeviceDialogComponent } from './assign-device-dialog/assign-device-dialog.component';
import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import { GridCellDirective } from '../../shared/components/grid/directives/grid-cell.directive';
import { GridDetailDirective } from '../../shared/components/grid/directives/grid-detail.directive';
import { LabelBadge, LabelBadgeDot } from '../../shared/components/label-badge/label-badge';
import {
  GridColumn,
  GridRowAction,
  GridTab,
  gridColumns,
} from '../../shared/components/grid/models/grid.model';
import { Button } from '../../shared/components/button/button';

/** Library tab. */
type LibraryTab = 'templates' | 'groups';

/** Which entity the detail pane is inspecting. */
type Detail = { type: 'template'; id: number } | { type: 'group'; id: number } | null;

/**
 * A `Layout` flattened together with its owning group, for the flat template
 * grid — the API only exposes layouts per group (embedded on `LayoutGroup`),
 * so the Library's cross-group template view is assembled client-side.
 */
interface TemplateRow {
  layout: Layout;
  group: LayoutGroup;
}

/** Event → pill styling. Maps the entity's activationEvent to a semantic tone. */
type PillTone = 'brand' | 'purple' | 'warning' | 'danger' | 'neutral';

/**
 * Layouts — Library screen, built on the universal grid.
 *
 * One `app-grid` drives both the Templates and Groups views via its native tab
 * strip; columns, data, row id and row actions swap per active tab, and the
 * grid's own overlay detail pane renders the template/group inspector. In the
 * design's vocabulary a "template" is a `Layout` and a "group" is a
 * `LayoutGroup`; that naming is surfaced in the UI, the data model is unchanged.
 *
 * Scope note: this is the Library only. The design's template-config,
 * group-editor, assign-to-displays and display-detail screens are deferred —
 * editing routes through the existing dialogs. Screen-content thumbnails,
 * "on N displays" counts and physical size/orientation have no backing API
 * field, so they are omitted rather than faked (the thumbnail cell shows the
 * grid's neutral placeholder).
 */
@Component({
  selector: 'app-layout-management',
  standalone: true,
  imports: [
    Grid,
    GridPage,
    GridCellDirective,
    GridDetailDirective,
    TranslocoDirective,
    LayoutGroupDialogComponent,
    LayoutDialogComponent,
    AssignDeviceDialogComponent,
    LabelBadge,
    Button,
  ],
  templateUrl: './layout-management.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutManagementComponent implements OnInit {
  private readonly layoutService = inject(LayoutService);
  private readonly toastService = inject(ToastService);
  private readonly modal = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);

  /** Namespaced translate helper. */
  private readonly t = (key: string, params?: Record<string, unknown>): string =>
    this.transloco.translate(`layouts.${key}`, params);

  readonly groups = signal<LayoutGroup[]>([]);
  readonly isLoading = signal(false);

  readonly tab = signal<LibraryTab>('templates');
  readonly query = signal('');
  readonly detail = signal<Detail>(null);

  // Dialog state (reused editors — full-page editors are deferred).
  readonly isGroupDialogOpen = signal(false);
  readonly groupDialogMode = signal<DialogMode>('add');
  readonly editingGroup = signal<LayoutGroup | null>(null);

  readonly isLayoutDialogOpen = signal(false);
  readonly layoutDialogMode = signal<LayoutDialogMode>('add');
  readonly editingLayout = signal<Layout | null>(null);
  /** The group a new/edited layout belongs to (dialogs need the parent group). */
  readonly layoutDialogGroup = signal<LayoutGroup | null>(null);

  readonly isAssignDialogOpen = signal(false);
  readonly assignGroup = signal<LayoutGroup | null>(null);

  /** Grid tab strip. Computed so labels re-translate on a language switch. */
  readonly gridTabs = computed<GridTab[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    return [
      { id: 'templates', label: this.t('tabs.templates') },
      { id: 'groups', label: this.t('tabs.groups') },
    ];
  });

  // ── Row-id + narrowing helpers (grid erases its generic in the host template) ──
  readonly templateRowId = (r: TemplateRow) => String(r.layout.id);
  readonly groupRowId = (g: LayoutGroup) => String(g.id);
  readonly activeRowId = computed<(row: unknown) => string>(() =>
    this.tab() === 'templates'
      ? (row) => this.templateRowId(row as TemplateRow)
      : (row) => this.groupRowId(row as LayoutGroup),
  );
  readonly asTemplate = (row: unknown): TemplateRow => row as TemplateRow;
  readonly asGroup = (row: unknown): LayoutGroup => row as LayoutGroup;

  // ── Columns per tab (computed so labels re-translate on a language switch) ──
  private readonly templateColumns = computed<GridColumn<TemplateRow>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    return gridColumns<TemplateRow>([
      { key: 'thumb', label: '', type: 'thumbnail', sortable: false, width: 72, thumbnailSrc: () => null },
      { key: 'name', label: this.t('templateColumns.template'), type: 'template', template: 'templateName', sortable: false, alwaysVisible: true, width: 260 },
      { key: 'event', label: this.t('templateColumns.showsWhen'), type: 'template', template: 'event', sortable: false, width: 180 },
      { key: 'group', label: this.t('templateColumns.group'), type: 'text', sortable: false, width: 180, value: (r) => r.group.groupId },
      { key: 'duration', label: this.t('templateColumns.duration'), type: 'text', sortable: false, width: 110, value: (r) => this.templateDuration(r.layout) },
    ]);
  });

  private readonly groupColumns = computed<GridColumn<LayoutGroup>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    return gridColumns<LayoutGroup>([
      { key: 'groupId', label: this.t('groupColumns.group'), type: 'text', textVariant: 'strong', sortable: false, alwaysVisible: true, width: 220 },
      { key: 'schedule', label: this.t('groupColumns.schedule'), type: 'text', sortable: false, width: 300, value: (g) => this.scheduleSummary(g) },
      { key: 'templates', label: this.t('groupColumns.templates'), type: 'text', sortable: false, width: 110, value: (g) => String(this.templatesInGroup(g).length) },
      { key: 'slideshow', label: this.t('groupColumns.slideshow'), type: 'text', sortable: false, width: 140, value: (g) => this.slideshowLabel(g) },
    ]);
  });

  readonly activeColumns = computed<GridColumn<unknown>[]>(() =>
    (this.tab() === 'templates' ? this.templateColumns() : this.groupColumns()) as GridColumn<unknown>[],
  );

  // ── Row actions per tab (computed so labels re-translate on a language switch) ──
  private readonly templateActions = computed<GridRowAction<TemplateRow>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    return [
      { id: 'edit', label: this.t('actions.configureTemplate'), placement: 'menu' },
      { id: 'edit-layout', label: this.t('actions.editLayout'), placement: 'menu' },
      { id: 'delete', label: this.t('actions.delete'), placement: 'menu', danger: true },
    ];
  });
  private readonly groupActions = computed<GridRowAction<LayoutGroup>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    return [
      { id: 'edit', label: this.t('actions.configureGroup'), placement: 'menu' },
      { id: 'assign', label: this.t('actions.assign'), placement: 'menu' },
      { id: 'delete', label: this.t('actions.delete'), placement: 'menu', danger: true },
    ];
  });
  readonly activeRowActions = computed<GridRowAction<unknown>[]>(() =>
    (this.tab() === 'templates' ? this.templateActions() : this.groupActions()) as GridRowAction<unknown>[],
  );

  // ── Data per tab ──────────────────────────────────────────────────────

  /** All templates flattened across the loaded groups. */
  private readonly allTemplates = computed<TemplateRow[]>(() =>
    this.groups().flatMap((group) =>
      (group.layouts ?? []).map((layout) => ({ layout, group })),
    ),
  );

  /** Templates after search filtering, ordered by group then sequence. */
  private readonly templates = computed<TemplateRow[]>(() => {
    const q = this.query().trim().toLowerCase();
    return this.allTemplates()
      .filter(({ layout }) => !q || layout.layoutId.toLowerCase().includes(q))
      .sort((a, b) => {
        if (a.group.groupId !== b.group.groupId) {
          return a.group.groupId.localeCompare(b.group.groupId);
        }
        return (a.layout.groupOrderNum ?? 99) - (b.layout.groupOrderNum ?? 99);
      });
  });

  /** Groups after search filtering. */
  private readonly filteredGroups = computed<LayoutGroup[]>(() => {
    const q = this.query().trim().toLowerCase();
    return q ? this.groups().filter((g) => g.groupId.toLowerCase().includes(q)) : this.groups();
  });

  readonly activeData = computed<readonly unknown[]>(() =>
    this.tab() === 'templates' ? this.templates() : this.filteredGroups(),
  );

  /** The row the grid should show selected/highlighted, per the active tab. */
  readonly selectedRow = computed<unknown>(() => {
    const d = this.detail();
    if (d?.type === 'template' && this.tab() === 'templates') {
      return this.allTemplates().find((t) => t.layout.id === d.id) ?? null;
    }
    if (d?.type === 'group' && this.tab() === 'groups') {
      return this.groups().find((g) => g.id === d.id) ?? null;
    }
    return null;
  });

  // ── Detail pane lookups ───────────────────────────────────────────────
  readonly detailTemplate = computed<TemplateRow | null>(() => {
    const d = this.detail();
    if (d?.type !== 'template') return null;
    return this.allTemplates().find((t) => t.layout.id === d.id) ?? null;
  });
  readonly detailGroup = computed<LayoutGroup | null>(() => {
    const d = this.detail();
    if (d?.type !== 'group') return null;
    return this.groups().find((g) => g.id === d.id) ?? null;
  });

  ngOnInit(): void {
    this.loadGroups();
  }

  private loadGroups(): void {
    this.isLoading.set(true);
    // Pull a generous first page: the Library is a flat cross-group view and
    // the groups endpoint is the only source of embedded layouts. Server-side
    // paging of the flattened template list is a follow-up (needs an endpoint).
    this.layoutService
      .getLayoutGroups(0, 100)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.groups.set(res.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.t('toast.loadFailed'), 'error');
          this.isLoading.set(false);
        },
      });
  }

  // ── Grid event handlers ───────────────────────────────────────────────

  onTabChange(tabId: string): void {
    this.tab.set(tabId as LibraryTab);
    this.query.set('');
    this.detail.set(null);
  }

  onSearch(term: string): void {
    this.query.set(term);
  }

  onRowClick(row: unknown): void {
    if (this.tab() === 'templates') {
      this.detail.set({ type: 'template', id: this.asTemplate(row).layout.id });
    } else {
      this.detail.set({ type: 'group', id: this.asGroup(row).id });
    }
  }

  onRowDblClick(row: unknown): void {
    if (this.tab() === 'templates') {
      this.editTemplate(this.asTemplate(row));
    } else {
      this.editGroup(this.asGroup(row));
    }
  }

  closeDetail(): void {
    this.detail.set(null);
  }

  onRowAction(actionId: string, row: unknown): void {
    if (this.tab() === 'templates') {
      const t = this.asTemplate(row);
      if (actionId === 'edit') this.editTemplate(t);
      else if (actionId === 'edit-layout') this.editLayout();
      else if (actionId === 'delete') void this.confirmDeleteTemplate(t);
    } else {
      const g = this.asGroup(row);
      if (actionId === 'edit') this.editGroup(g);
      else if (actionId === 'assign') this.openAssignDialog(g);
      else if (actionId === 'delete') void this.confirmDeleteGroup(g);
    }
  }

  // ── Display helpers ───────────────────────────────────────────────────

  /** Collapse the raw activation event into a filter/pill class. */
  eventClass(event: string | null): 'DEFAULT' | 'BUTTON' | 'OFFLINE' | 'ONLINE' | 'OTHER' {
    if (!event) return 'DEFAULT';
    if (event.startsWith('BUTTON')) return 'BUTTON';
    if (event === 'OFFLINE') return 'OFFLINE';
    if (event === 'ONLINE') return 'ONLINE';
    if (event === 'DEFAULT') return 'DEFAULT';
    return 'OTHER';
  }

  /** Human label for the event pill. */
  eventLabel(layout: Layout): string {
    switch (this.eventClass(layout.activationEvent)) {
      case 'DEFAULT':
        return this.t('event.default');
      case 'BUTTON': {
        const buttons = layout.activationButtons?.trim();
        return buttons ? this.t('event.buttonNamed', { buttons }) : this.t('event.button');
      }
      case 'OFFLINE': {
        const delay = layout.activationOfflineDelay;
        return delay != null
          ? this.t('event.offlineDelay', { minutes: Math.round(delay / 60) })
          : this.t('event.offline');
      }
      case 'ONLINE':
        return this.t('event.online');
      default:
        return layout.activationEvent ?? this.t('event.default');
    }
  }

  /** Semantic tone for the event pill. */
  eventTone(layout: Layout): PillTone {
    switch (this.eventClass(layout.activationEvent)) {
      case 'DEFAULT':
        return 'brand';
      case 'BUTTON':
        return 'purple';
      case 'OFFLINE':
        return 'warning';
      case 'ONLINE':
        return 'neutral';
      default:
        return 'neutral';
    }
  }

  /** LabelBadge dot colour for the event pill tone. */
  eventDot(layout: Layout): LabelBadgeDot | null {
    switch (this.eventTone(layout)) {
      case 'brand':
        return 'blue';
      case 'purple':
        return 'purple';
      case 'warning':
        return 'orange';
      case 'danger':
        return 'red';
      default:
        return null;
    }
  }

  /** Human day-set label from a group's comma-separated day string. */
  daysLabel(group: LayoutGroup): string {
    if (!group.days) return this.t('days.everyDay');
    const norm = group.days
      .split(',')
      .map((d) => d.trim().slice(0, 3).toUpperCase())
      .filter(Boolean);
    if (norm.length === 0 || norm.length === 7) return this.t('days.everyDay');
    const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    const weekend = ['SAT', 'SUN'];
    if (norm.length === 5 && weekdays.every((d) => norm.includes(d))) return this.t('days.weekdays');
    if (norm.length === 2 && weekend.every((d) => norm.includes(d))) return this.t('days.weekend');
    return norm.map((d) => d[0] + d.slice(1).toLowerCase()).join(', ');
  }

  /** One-line group schedule summary. */
  scheduleSummary(group: LayoutGroup): string {
    const window =
      group.startTime === '00:00' && (group.endTime === '23:59' || group.endTime === '24:00')
        ? this.t('schedule.allDay')
        : group.startTime && group.endTime
          ? `${group.startTime}–${group.endTime}`
          : this.t('schedule.anyTime');
    const range =
      group.startDate || group.endDate
        ? ` · ${group.startDate ?? '—'} → ${group.endDate ?? '—'}`
        : '';
    return `${this.daysLabel(group)} · ${window}${range}`;
  }

  slideshowLabel(group: LayoutGroup): string {
    if (!group.isStateSlideshow) return this.t('slideshow.off');
    const ms = group.stateSlideshowChangePeriod;
    return ms != null
      ? this.t('slideshow.every', { seconds: (ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1) })
      : this.t('slideshow.on');
  }

  templateDuration(layout: Layout): string {
    const ms = layout.slideshowDuration;
    return ms != null
      ? this.t('duration.seconds', { seconds: (ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1) })
      : '—';
  }

  templatesInGroup(group: LayoutGroup): Layout[] {
    return [...(group.layouts ?? [])].sort(
      (a, b) => (a.groupOrderNum ?? 99) - (b.groupOrderNum ?? 99),
    );
  }

  // ── Group CRUD ────────────────────────────────────────────────────────

  openCreateGroupDialog(): void {
    this.groupDialogMode.set('add');
    this.editingGroup.set(null);
    this.isGroupDialogOpen.set(true);
  }

  /** Primary action: adds a template, or a group when on the Groups tab. */
  onPrimaryAction(): void {
    if (this.tab() === 'groups') this.openCreateGroupDialog();
    else this.openCreateTemplateDialog();
  }

  editGroup(group: LayoutGroup): void {
    this.groupDialogMode.set('edit');
    this.editingGroup.set(group);
    this.isGroupDialogOpen.set(true);
  }

  closeGroupDialog(): void {
    this.isGroupDialogOpen.set(false);
    this.editingGroup.set(null);
  }

  handleGroupSave(request: LayoutGroupRequest): void {
    const mode = this.groupDialogMode();
    const action$ =
      mode === 'add'
        ? this.layoutService.createLayoutGroup(request)
        : this.layoutService.updateLayoutGroup(this.editingGroup()!.id, request);

    action$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show(
          mode === 'add' ? this.t('toast.groupCreated') : this.t('toast.groupUpdated'),
          'success',
        );
        this.closeGroupDialog();
        this.loadGroups();
      },
      error: () => this.toastService.show(this.t('toast.groupSaveFailed'), 'error'),
    });
  }

  async confirmDeleteGroup(group: LayoutGroup): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.t('deleteGroup.title'),
      message: this.t('deleteGroup.message', { name: group.groupId }),
      confirmLabel: this.transloco.translate('common.delete'),
      confirmWord: group.groupId,
    });
    if (confirmed) this.deleteGroup(group);
  }

  private deleteGroup(group: LayoutGroup): void {
    this.layoutService
      .deleteLayoutGroup(group.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('toast.groupDeleted'), 'success');
          if (this.detail()?.type === 'group' && this.detail()?.id === group.id) {
            this.detail.set(null);
          }
          this.loadGroups();
        },
        error: () => this.toastService.show(this.t('toast.groupDeleteFailed'), 'error'),
      });
  }

  // ── Template (Layout) CRUD ────────────────────────────────────────────

  openCreateTemplateDialog(): void {
    // A new template needs a target group; default to the inspected or first one.
    const group = this.detailGroup() ?? this.groups()[0] ?? null;
    if (!group) {
      this.toastService.show(this.t('toast.needGroup'), 'error');
      return;
    }
    this.layoutDialogMode.set('add');
    this.editingLayout.set(null);
    this.layoutDialogGroup.set(group);
    this.isLayoutDialogOpen.set(true);
  }

  editTemplate(row: TemplateRow): void {
    this.layoutDialogMode.set('edit');
    this.editingLayout.set(row.layout);
    this.layoutDialogGroup.set(row.group);
    this.isLayoutDialogOpen.set(true);
  }

  /** Placeholder for the not-yet-built visual layout editor. */
  editLayout(): void {
    this.toastService.show(this.t('toast.editLayoutUnderConstruction'), 'info');
  }

  closeLayoutDialog(): void {
    this.isLayoutDialogOpen.set(false);
    this.editingLayout.set(null);
    this.layoutDialogGroup.set(null);
  }

  handleLayoutSave(request: LayoutRequest): void {
    const mode = this.layoutDialogMode();
    const group = this.layoutDialogGroup();
    if (!group) return;
    const action$ =
      mode === 'add'
        ? this.layoutService.createLayout(group.id, request)
        : this.layoutService.updateLayout(this.editingLayout()!.id, request);

    action$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show(
          mode === 'add' ? this.t('toast.templateCreated') : this.t('toast.templateUpdated'),
          'success',
        );
        this.closeLayoutDialog();
        this.loadGroups();
      },
      error: () => this.toastService.show(this.t('toast.templateSaveFailed'), 'error'),
    });
  }

  async confirmDeleteTemplate(row: TemplateRow): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.t('deleteTemplate.title'),
      message: this.t('deleteTemplate.message', { name: row.layout.layoutId }),
      confirmLabel: this.transloco.translate('common.delete'),
    });
    if (confirmed) this.deleteTemplate(row.layout);
  }

  private deleteTemplate(layout: Layout): void {
    this.layoutService
      .deleteLayout(layout.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('toast.templateDeleted'), 'success');
          if (this.detail()?.type === 'template' && this.detail()?.id === layout.id) {
            this.detail.set(null);
          }
          this.loadGroups();
        },
        error: () => this.toastService.show(this.t('toast.templateDeleteFailed'), 'error'),
      });
  }

  // ── Assign to displays ────────────────────────────────────────────────

  openAssignDialog(group: LayoutGroup): void {
    this.assignGroup.set(group);
    this.isAssignDialogOpen.set(true);
  }

  closeAssignDialog(): void {
    this.isAssignDialogOpen.set(false);
    this.assignGroup.set(null);
  }
}
